import firebase from 'firebase/app';
import invariant from 'tiny-invariant';
import { MachineOptions, assign, send } from 'xstate';

import { config } from 'src/config';
import { EmptyDesignConfig } from 'src/modules/designStudio/constants';
import { BuildingModel } from 'src/modules/designStudio/database/buildingModel';
import {
  batchMarkVersionsAsNotCurrent,
  getLatestVersions,
  getVersionMarkedAsCurrent,
  getVersionsToBeDeleted,
  listenForVersions,
  listenForVersionsBySession,
} from 'src/modules/designStudio/machines/firebaseUtils';
import { sharedActions } from 'src/modules/designStudio/machines/sharedActions';
import {
  Context,
  Event,
  SetHistoryEvent,
  SetupCompletionEvent,
  ShareDocument,
  UpdateDesignEvent,
  VersionDocument,
} from 'src/modules/designStudio/machines/types';
import { DesignConfig } from 'src/modules/designStudio/types';
import { client as apolloClient } from 'src/shared/graphql';
import { FileStorageScopes, User } from 'src/shared/graphql/generated';
import { mutations, queries } from 'src/shared/graphql/requests';
import { Route } from 'src/shared/route';
import { ProductSelected, productSelected } from 'src/shared/telemetry';

const FIREBASE_CONFIG = {
  apiKey: config.FIREBASE_API_KEY,
  authDomain: config.FIREBASE_AUTH_DOMAIN,
  projectId: config.FIREBASE_PROJECT_ID,
  storageBucket: config.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: config.FIREBASE_MESSAGING_SENDER_ID,
  appId: config.FIREBASE_APP_ID,
  measurementId: config.FIREBASE_MEASUREMENT_ID,
};
const isNotBottomOfStack = ({ currentIndexInHistory, history }: Context) =>
  !!(currentIndexInHistory && currentIndexInHistory !== 0 && history?.length);
const isNotTopOfStack = ({ currentIndexInHistory, history }: Context) =>
  !!(
    currentIndexInHistory !== undefined &&
    history?.length &&
    currentIndexInHistory !== history.length - 1
  );
const markVersionAsCurrent = (
  { history, currentIndexInHistory }: Context,
  direction: 1 | -1,
) => {
  if (
    !history ||
    !history.length ||
    (!currentIndexInHistory && currentIndexInHistory !== 0)
  ) {
    return true;
  }
  const batch = batchMarkVersionsAsNotCurrent(history);
  const versionToBeMarkedAsCurrent = history[currentIndexInHistory + direction];

  batch.update(versionToBeMarkedAsCurrent.ref, { current: true });
  return batch.commit();
};
const optimisticUpdate = (
  { currentIndexInHistory, history }: Context,
  direction: -1 | 1,
) => {
  if (!currentIndexInHistory && currentIndexInHistory !== 0) {
    return {};
  }

  const nextIndexInHistory = currentIndexInHistory + direction;
  const currentVersion = history?.[nextIndexInHistory];

  const designConfig = {
    ...EmptyDesignConfig,
    ...currentVersion?.design_config,
  };

  return {
    designConfig,
    currentVersion: currentVersion?.id,
  };
};
const createVersion = async (ctx: Context, designConfig: DesignConfig) => {
  invariant(ctx.buildingModel, 'Missing from context: building model');
  invariant(ctx.buildingModel.ref, 'Missing from context: building model ref');
  invariant(ctx.userId, 'Missing from context: userId');
  invariant(ctx.sessionId, 'Missing from context: sessionId');

  const batch = batchMarkVersionsAsNotCurrent(ctx.history);
  const newVersionRef = ctx.buildingModel.ref?.collection('versions').doc();

  if (newVersionRef) {
    batch.set(newVersionRef, {
      author_id: ctx.userId,
      created_at: firebase.firestore.Timestamp.now(),
      design_config: designConfig,
      session_id: ctx.sessionId,
      current: true,
    });
  }

  if (
    ctx.currentIndexInHistory !== undefined &&
    ctx.history &&
    ctx.currentIndexInHistory !== ctx.history.length - 1
  ) {
    const currentVersion = ctx.history[ctx.currentIndexInHistory];
    const versionsToBeDeleted = await getVersionsToBeDeleted(
      ctx.buildingModel.ref,
      currentVersion.created_at,
      ctx.userId,
      ctx.sessionId,
    );

    versionsToBeDeleted?.docs.forEach(versionToDelete =>
      batch.delete(versionToDelete.ref),
    );
  }
  return batch.commit();
};
const fetchCurrentVersion = async (ctx: Context) => {
  invariant(ctx.buildingModel, 'Missing from context: building model');
  invariant(ctx.buildingModel.ref, 'Missing from context: building model ref');
  invariant(ctx.userId, 'Missing from context: userId');
  let currentVersion;
  // fetch doc that has current boolean = true;
  const currentDocs = await getVersionMarkedAsCurrent(
    ctx.buildingModel.ref,
    ctx.userId,
  );

  if (currentDocs && !currentDocs?.empty) {
    [currentVersion] = currentDocs.docs;
  }

  if (!currentVersion) {
    // if no doc above, fetch latest doc
    const latestDocs = await getLatestVersions(
      ctx.buildingModel.ref,
      ctx.userId,
    );
    if (latestDocs && !latestDocs.empty) {
      [currentVersion] = latestDocs.docs;
    }
  }

  if (currentVersion) {
    await currentVersion.ref?.update({
      session_id: ctx.sessionId,
      current: true,
    });
  }
  // if no version history, create initial version with empty config
  if (!currentVersion) {
    await createVersion(ctx, EmptyDesignConfig);
  }

  return currentVersion;
};
const options: MachineOptions<Context, Event> = {
  services: {
    fetchCurrentVersion,
    markPreviousDesignAsCurrent: async (ctx: Context) =>
      markVersionAsCurrent(ctx, -1),
    markNextDesignAsCurrent: async (ctx: Context) =>
      markVersionAsCurrent(ctx, 1),
    setupAndFetchModelReference: async (ctx: Context) => {
      let userId: number | undefined;
      let email: string | undefined;
      let user: User | undefined;

      if (!ctx.authToken) {
        const response = await queries.profile(apolloClient, {});
        user = response.data.profile.user as User;

        userId = user.id;
        email = user.email;
      }

      invariant(ctx.jobId, 'No job id was provided');
      let token: string | undefined;

      if (ctx.authToken) {
        token = ctx.authToken;
      } else {
        const storageTokenResponse = await mutations.createStorageToken(
          apolloClient,
          { storageScope: FileStorageScopes.DesignStudio },
        );

        token = storageTokenResponse?.data?.createFileStorageToken?.token;
      }

      const firebaseApp = firebase.initializeApp(FIREBASE_CONFIG);

      if (token) {
        const { EMULATOR_FIREBASE_AUTH: emulatorHost } = config;
        const auth = firebase.auth(firebaseApp);

        if (emulatorHost) {
          auth.useEmulator(emulatorHost);
        }

        await auth.signInWithCustomToken(token);
      }
      const buildingModel = new BuildingModel(ctx.jobId);
      const modelRef = await buildingModel.getBuildingModelReference();

      if (!userId && ctx.shareToken) {
        const share = await modelRef
          ?.collection('shares')
          .doc(ctx.shareToken)
          .get();

        const { author_id: id } = share.data() as ShareDocument;
        userId = id;
      }

      return {
        user,
        email,
        firebaseApp,
        buildingModel,
        userId,
      };
    },
    fetchVersions: (ctx: Context) => (
      callback: (event: SetHistoryEvent) => void,
    ) => {
      const { buildingModel, userId, sessionId, shareToken } = ctx;
      invariant(buildingModel, 'Missing from context: building model');
      invariant(buildingModel.ref, 'Missing from context: building model ref');
      return shareToken
        ? listenForVersions({ ref: buildingModel.ref, userId, callback })
        : listenForVersionsBySession({
            ref: buildingModel.ref,
            userId,
            callback,
            sessionId,
          });
    },
    createVersion: async (ctx: Context, event: Event) => {
      let designConfig;
      if (event.type === 'RESET_DESIGN') {
        designConfig = {
          ...EmptyDesignConfig,
        };
      } else {
        designConfig = {
          ...ctx.designConfig,
          [(event as UpdateDesignEvent).category]: (event as UpdateDesignEvent)
            .update,
        };
      }

      await createVersion(ctx, designConfig);
    },
  },
  guards: {
    canUndo: isNotBottomOfStack,
    canRedo: isNotTopOfStack,
    isShareView: ctx => !!ctx.shareToken,
  },
  actions: {
    updateUndoRedoState: send('UPDATE_UNDO_REDO_STATE'),
    updateCanUndoAndRedo: assign(ctx => ({
      canUndo: isNotBottomOfStack(ctx),
      canRedo: isNotTopOfStack(ctx),
    })),
    // log error/exception to Sentry
    logError: sharedActions.logError,
    setupCompletion: assign((_: Context, event: Event) => {
      const {
        user,
        email,
        firebaseApp,
        buildingModel,
        userId,
      } = (event as SetupCompletionEvent).data;

      return {
        buildingModel,
        firebaseApp,
        userId,
        email,
        user,
      };
    }),
    setHistory: assign((_: Context, event: Event) => {
      const history: VersionDocument[] = [];
      let currentIndexInHistory;

      (event as SetHistoryEvent).data.forEach((versionDocument, index) => {
        // if theres already a current index, keep that as version
        // otherwise use the last index as current version
        const versionData = versionDocument.data();

        if (versionData.current) {
          currentIndexInHistory = index;
        }

        const version = {
          ...versionData,
          ref: versionDocument.ref,
          id: versionDocument.id,
        };

        history.push(version as VersionDocument);
      });

      if (currentIndexInHistory === undefined) {
        currentIndexInHistory = history.length - 1;
      }

      if (history.length !== undefined) {
        const design = history[currentIndexInHistory];
        // Need to make sure we are forward compatible to any changes to the design config
        // so use EmptyDesignConfig as basis then over lay any changes over it.
        const currentDesignConfig = {
          ...EmptyDesignConfig,
          ...design.design_config,
        };

        return {
          currentVersion: design.id,
          currentIndexInHistory,
          designConfig: currentDesignConfig,
          history,
        };
      }

      return {};
    }),
    updateDesignConfigOptimistically: assign((ctx: Context, event: Event) => {
      let designConfig;

      if (event.type === 'RESET_DESIGN') {
        designConfig = {
          ...EmptyDesignConfig,
        };
      } else {
        designConfig = {
          ...ctx.designConfig,
          [(event as UpdateDesignEvent).category]: (event as UpdateDesignEvent)
            .update,
        };
      }
      return { designConfig };
    }),
    undoDesignAction: assign((ctx: Context) => optimisticUpdate(ctx, -1)),
    redoDesignAction: assign((ctx: Context) => optimisticUpdate(ctx, 1)),
    trackUpdate: (ctx: Context, event: Event) => {
      const { trackingData } = event as UpdateDesignEvent;

      const trackData: ProductSelected = {
        page_or_screen_name: Route.DESIGN_STUDIO.page as string,
        job_id: ctx.jobId ?? undefined,
        ...trackingData,
      };

      try {
        productSelected(trackData);
      } catch (e) {
        sharedActions.logError(ctx, { type: '', data: e as Error });
      }
    },
    teardownFirebaseApp: async (ctx: Context) => {
      await firebase.auth().signOut();
      return ctx.firebaseApp?.delete();
    },
  },
  delays: {},
  activities: {},
};
export { fetchCurrentVersion, options, createVersion };
